import { Box, Checkbox, FormControlLabel, FormHelperText, Tooltip, TypographyProps } from "@mui/material";
import React, { type JSX } from 'react';
import { Controller, ControllerRenderProps, FieldPath, FieldValues, RegisterOptions, useFormContext } from "react-hook-form";
import { FormFieldPropsBase } from "../form/Form";

export type FormCheckboxProps<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
    onChange?: ((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void) | undefined,
    rules?: Omit<RegisterOptions<TFieldValues, TName>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
    tooltip?: string | JSX.Element;
    typographyProps?: TypographyProps
    icon?: React.ReactNode;
    checkedIcon?: React.ReactNode;
    size?: 'small' | 'medium' | undefined
} & FormFieldPropsBase<TFieldValues, TName>;

export default function FormCheckbox<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
    name,
    id,
    testId,
    label,
    ariaLabel,
    // readOnly,
    helperText,
    onChange: onChangeCallback,
    sx,
    disabled,
    rules,
    tooltip,
    typographyProps,
    icon,
    checkedIcon,
    size
}: FormCheckboxProps<TFieldValues, TName>): JSX.Element {
    const { control } = useFormContext<TFieldValues>();

    function renderFormControlLabel<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
        onChange,
        onBlur,
        value,
        disabled,
        //name,
        ref
    }: ControllerRenderProps<TFieldValues, TName>): JSX.Element {
        return (
            <FormControlLabel
                aria-label={ariaLabel as string}
                sx={{
                    marginTop: 0,
                    ...sx
                }}
                control={
                    <Checkbox
                        size={size}
                        //color="secondary"
                        id={id}
                        data-testid={testId}
                        checked={value}
                        onChange={
                            (event, checked) => {
                                onChange(event, checked);
                                onChangeCallback?.(event, checked);
                            }
                        }
                        onBlur={onBlur}
                        disabled={disabled}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        slotProps={{
                            input: {
                                ref: ref
                            }
                        }}
                    />
                }
                label={label ?? ''}
                labelPlacement="end"

                slotProps={{
                    typography: typographyProps
                }}
            />
        );
    }

    return (
        <Controller
            control={control}
            rules={rules}
            name={name}
            render={
                ({
                    field: {
                        ref,
                        name,
                        value,
                        onChange,
                        onBlur
                    },
                    // fieldState: { invalid, isTouched, isDirty, error },
                    // formState
                }) => (
                    <>
                        {tooltip &&
                            <Tooltip title={tooltip}>
                                <Box component='span'>
                                    {renderFormControlLabel({
                                        ref,
                                        value,
                                        onChange,
                                        onBlur,
                                        disabled,
                                        name
                                    })}
                                </Box>
                            </Tooltip>
                        }

                        {!tooltip &&
                            renderFormControlLabel({
                                ref,
                                value,
                                onChange,
                                onBlur,
                                disabled,
                                name
                            })
                        }

                        {helperText &&
                            <FormHelperText>{helperText}</FormHelperText>
                        }
                    </>
                )
            }
        />
    );
}